import type { FC } from "react";
import { Card, Flex, Gauge, Text } from "@huub-web/ui";
import { useTranslation } from "react-i18next";

import {
  allToLowerStyle,
  bannerCardContentStyle,
  bannerCardInnerStyle,
  gaugeStyle,
} from "./GaugeCard.css";

export type GaugeCardProps = {
  name: string;
  grade: number;
  loading?: boolean;
};

export const GaugeCard: FC<GaugeCardProps> = ({ name, grade, loading }) => {
  const { t } = useTranslation();

  const value: [number, number] = (() => {
    if (loading) {
      return [0, 0] as [number, number];
    }
    if (grade >= 75) {
      return [75, 100] as [number, number];
    }
    if (grade >= 50) {
      return [50, 75] as [number, number];
    }
    if (grade >= 25) {
      return [25, 50] as [number, number];
    }
    return [0, 25] as [number, number];
  })();

  const status = (() => {
    if (grade >= 80) {
      return "Uitstekend";
    }
    if (grade >= 60) {
      return "Goed";
    }
    if (grade >= 40) {
      return "Redelijk";
    }
    return "Matig";
  })();

  return (
    <Card
      border="backgroundThick"
      elevated
      variant="tertiary"
      innerClassName={bannerCardInnerStyle}
      contentClassName={bannerCardContentStyle}
      padding="s"
    >
      <Flex direction="column" gap="xs" grow>
        <Text variant="bodyM" bold>
          Indruk van <span className={allToLowerStyle}>{name}</span>
        </Text>
        <Flex gap="xs" align="baseline">
          <Text variant="surfaceSubtitle" color="currentColor50">
            Status
          </Text>
          <Text variant="bodyS" bold={!loading} italic={loading}>
            {loading ? t("analysis.loading") : status}
          </Text>
        </Flex>
      </Flex>
      <Gauge
        value={value}
        pointerValue={loading ? 50 : grade}
        max={100}
        size="displayM"
        className={gaugeStyle}
        subdivisions={4}
      />
    </Card>
  );
};
